/**
 * 在庫管理システム型定義
 */

import { THEME_COLORS } from '@/shared/constants/colors';

/** 在庫操作種別 */
export const INVENTORY_TRANSACTION_TYPE = {
  INBOUND: 'INBOUND',
  CONSUMPTION: 'CONSUMPTION'
} as const;

export type InventoryTransactionType = typeof INVENTORY_TRANSACTION_TYPE[keyof typeof INVENTORY_TRANSACTION_TYPE];

/**
 * 入庫(在庫調整-増)理由コード
 *
 * 設計書 PR-7 で `NEW_PURCHASE` をユーザー直接選択経路から除外(発注ワークフロー
 * `applyInventoryIncrease` 経由のみ)。faro-api `INBOUND_REASON_CODE` と整合。
 */
export const INBOUND_REASON_CODE = {
  ADJUSTMENT_IN: 'ADJUSTMENT_IN',
  OTHER_IN: 'OTHER_IN'
} as const;

export type InboundReasonCode = typeof INBOUND_REASON_CODE[keyof typeof INBOUND_REASON_CODE];

/** 消費理由コード */
export const CONSUMPTION_REASON_CODE = {
  NORMAL_USE: 'NORMAL_USE',
  DISPOSAL: 'DISPOSAL',
  ADJUSTMENT_OUT: 'ADJUSTMENT_OUT',
  OTHER_OUT: 'OTHER_OUT'
} as const;

export type ConsumptionReasonCode = typeof CONSUMPTION_REASON_CODE[keyof typeof CONSUMPTION_REASON_CODE];

/**
 * システム経由(発注ワークフロー)で付与される理由コード
 *
 * `NEW_PURCHASE`(着荷確認時) / `RETURN`(返品入庫) / `ARRIVAL_REVERSAL`(着荷取消)。
 * faro-api `INCREASE_REASON_CODE` / `DECREASE_REASON_CODE` のうちユーザー直接選択不可な値。
 */
export const SYSTEM_REASON_CODE = {
  NEW_PURCHASE: 'NEW_PURCHASE',
  RETURN: 'RETURN',
  ARRIVAL_REVERSAL: 'ARRIVAL_REVERSAL'
} as const;

export type SystemReasonCode = typeof SYSTEM_REASON_CODE[keyof typeof SYSTEM_REASON_CODE];

/**
 * 全理由コードのユニオン型
 *
 * ユーザー直接選択(`InboundReasonCode` / `ConsumptionReasonCode`)+ システム経由(`SystemReasonCode`)。
 * 履歴表示等で reasonCode → 表示名を引く場合の網羅的マップ用。
 */
export type AllReasonCode = InboundReasonCode | ConsumptionReasonCode | SystemReasonCode;

/**
 * 理由コード表示名
 *
 * 履歴一覧は API レスポンスの `reasonDisplayName` を直接使用するため、本マップは
 * UI 側で reasonCode から表示名を引きたい場合のフォールバックとして全コード網羅。
 *
 * 表記注意: 本マップは「過去の履歴表記」(全角括弧「（増）」「（減）」)を保持する。
 * 一方、`ADJUSTMENT_REASON_OPTIONS` は新モーダルの選択肢ラベルとして「調整(増)」
 * (半角括弧 + 増減併記スタイル)を独自に定義する。これは過去履歴の表示崩れを防ぐ
 * ための意図的な二重管理(SoT は用途別に分離: 履歴フォールバック vs 入力 UI)。
 */
export const REASON_CODE_DISPLAY_NAME: Record<AllReasonCode, string> = {
  NEW_PURCHASE: '新規購入',
  RETURN: '返品',
  ADJUSTMENT_IN: '調整（増）',
  OTHER_IN: 'その他',
  NORMAL_USE: '通常消費',
  DISPOSAL: '廃棄',
  ADJUSTMENT_OUT: '調整（減）',
  OTHER_OUT: 'その他',
  ARRIVAL_REVERSAL: '入庫取消'
} as const;

/**
 * 「在庫調整」モーダルで選択可能な理由コード(増減両方含む)
 *
 * v1.6 で旧「在庫調整(増)」と「消費」を再編し、減側のうち以下を本モーダルへ統合:
 *  - `ADJUSTMENT_OUT`(調整(減))
 *  - `OTHER_OUT`(その他(減)、対称性確保のため v1.6.1 で消費側から移動)
 * 消費モーダルに残るのは `NORMAL_USE`(通常消費) / `DISPOSAL`(廃棄) のみ。
 * サーバー API・DB enum は変更せず、フロントの振り分けで実現する。
 */
export const ADJUSTMENT_REASON_CODE = {
  ADJUSTMENT_IN: 'ADJUSTMENT_IN',
  OTHER_IN: 'OTHER_IN',
  ADJUSTMENT_OUT: 'ADJUSTMENT_OUT',
  OTHER_OUT: 'OTHER_OUT',
} as const;

export type AdjustmentReasonCode =
  typeof ADJUSTMENT_REASON_CODE[keyof typeof ADJUSTMENT_REASON_CODE];

/** 在庫調整方向(増 / 減) */
export type AdjustmentDirection = 'increase' | 'decrease';

interface AdjustmentReasonOption {
  readonly value: AdjustmentReasonCode;
  readonly label: string;
  readonly direction: AdjustmentDirection;
  readonly tooltip: string;
}

/** 在庫調整理由選択肢(増減を区別する表記 + Tooltip 説明、UI 選択肢の SoT) */
export const ADJUSTMENT_REASON_OPTIONS: readonly AdjustmentReasonOption[] = [
  {
    value: ADJUSTMENT_REASON_CODE.ADJUSTMENT_IN,
    label: '調整(増)',
    direction: 'increase',
    tooltip: '棚卸結果による不一致補正で在庫を増やす場合',
  },
  {
    value: ADJUSTMENT_REASON_CODE.OTHER_IN,
    label: 'その他(増)',
    direction: 'increase',
    tooltip: '上記に当てはまらない特殊な入庫(備考に詳細記載)',
  },
  {
    value: ADJUSTMENT_REASON_CODE.ADJUSTMENT_OUT,
    label: '調整(減)',
    direction: 'decrease',
    tooltip: '棚卸結果による不一致補正で在庫を減らす場合',
  },
  {
    value: ADJUSTMENT_REASON_CODE.OTHER_OUT,
    label: 'その他(減)',
    direction: 'decrease',
    tooltip: '上記に当てはまらない特殊な減少(備考に詳細記載)',
  },
] as const;

/**
 * 在庫調整理由から増減方向を判定
 *
 * `ADJUSTMENT_IN` / `OTHER_IN` → 'increase'(`recordInventoryAdjustment` 経由で在庫加算)
 * `ADJUSTMENT_OUT` / `OTHER_OUT` → 'decrease'(`recordConsumption` 経由で在庫減算)
 */
export function getAdjustmentDirection(params: {
  readonly reasonCode: AdjustmentReasonCode;
}): AdjustmentDirection {
  if (isIncreaseAdjustmentReason({ reasonCode: params.reasonCode })) {
    return 'increase';
  }
  return 'decrease';
}

/**
 * 在庫調整理由が増側 (`InboundReasonCode`) かどうかの型ガード
 *
 * 振り分け処理で `recordInventoryAdjustment` の `InventoryAdjustmentRequest`
 * (reasonCode: InboundReasonCode) に narrow するために使う。
 * generic 化することで呼出側の入力オブジェクト全体を増側 reasonCode に narrow する。
 */
export function isIncreaseAdjustmentReason<
  T extends { readonly reasonCode: AdjustmentReasonCode },
>(params: T): params is T & { readonly reasonCode: InboundReasonCode } {
  return (
    params.reasonCode === ADJUSTMENT_REASON_CODE.ADJUSTMENT_IN ||
    params.reasonCode === ADJUSTMENT_REASON_CODE.OTHER_IN
  );
}

/**
 * 在庫調整理由が減側 (`ADJUSTMENT_OUT` / `OTHER_OUT`) かどうかの型ガード
 *
 * `recordConsumption` の `ConsumeRequest`(reasonCode: ConsumptionReasonCode)に
 * narrow するために使う。両者は `ConsumptionReasonCode` のサブセット。
 */
export function isDecreaseAdjustmentReason<
  T extends { readonly reasonCode: AdjustmentReasonCode },
>(params: T): params is T & { readonly reasonCode: ConsumptionReasonCode } {
  return (
    params.reasonCode === ADJUSTMENT_REASON_CODE.ADJUSTMENT_OUT ||
    params.reasonCode === ADJUSTMENT_REASON_CODE.OTHER_OUT
  );
}

interface ConsumptionReasonOption {
  readonly value: ConsumptionReasonCode;
  readonly label: string;
  readonly tooltip: string;
}

/**
 * 消費理由選択肢(v1.6.1: `OTHER_OUT` は「在庫調整」モーダル側に移動)
 *
 * 「実際に使った(NORMAL_USE)」「廃棄した(DISPOSAL)」のみに絞り、その他系の減少は
 * 在庫調整モーダルで `OTHER_OUT`(その他(減))を選んで処理する。
 */
export const CONSUMPTION_REASON_OPTIONS: readonly ConsumptionReasonOption[] = [
  {
    value: CONSUMPTION_REASON_CODE.NORMAL_USE,
    label: '通常消費',
    tooltip: '製造工程など本来の用途で使用した場合',
  },
  {
    value: CONSUMPTION_REASON_CODE.DISPOSAL,
    label: '廃棄',
    tooltip: '使用せずに廃棄した場合(期限切れ・品質不良など)',
  },
] as const;

/** 材料種別 */
export interface MaterialType {
  readonly id: number;
  readonly code: string;
  readonly displayName: string;
  readonly displayOrder: number;
  readonly isActive: boolean;
}

/** 在庫データ（単一材料） */
export interface InventoryItem {
  readonly materialTypeId: number;
  readonly materialTypeCode: string;
  readonly materialTypeName: string;
  readonly currentQuantity: number;
  readonly minimumThreshold: number;
  readonly reorderThreshold: number;
  readonly isLowStock: boolean;
  readonly isReorderRequired: boolean;
  readonly lastTransactionAt: Date | null;
  readonly lastTransactionUserName: string | null;
  /** 発注中(ORDERED ステータス)の数量(設計書 PR-6 / PR-11 §6.3.1) */
  readonly pendingOrderedQuantity: number;
  /** 効果在庫(現在在庫 + 発注中、設計書 PR-6) */
  readonly effectiveQuantity: number;
  /** 着荷予定日経過の発注が存在するか(設計書 PR-11 §6.3.2) */
  readonly hasOverdueOrders: boolean;
}

/** 現場の在庫データ(在庫は現場単位で管理し、現場内の盤で共有する) */
export interface SiteInventoryData {
  readonly siteId: number;
  readonly siteName: string;
  readonly inventories: readonly InventoryItem[];
}

/**
 * 在庫調整(増)リクエスト
 *
 * 設計書 PR-7 で `InboundRequest` からリネーム。`reasonCode` から `NEW_PURCHASE` を除外し、
 * ユーザー直接の在庫調整経路として使用する。
 */
export interface InventoryAdjustmentRequest {
  readonly materialTypeId: number;
  readonly quantity: number;
  readonly reasonCode: InboundReasonCode;
  readonly note?: string;
}

/** 消費リクエスト */
export interface ConsumeRequest {
  readonly materialTypeId: number;
  readonly quantity: number;
  readonly reasonCode: ConsumptionReasonCode;
  readonly note?: string;
}

/** 閾値更新リクエスト */
export interface UpdateThresholdsRequest {
  readonly materialTypeId: number;
  readonly minimumThreshold: number;
  readonly reorderThreshold: number;
}

/** トランザクション結果 */
export interface TransactionResult {
  readonly transactionId: number;
  readonly siteId: number;
  /** 消費時に盤を指定した場合のみ値を持つ */
  readonly panelId: number | null;
  readonly materialTypeId: number;
  readonly materialTypeName: string;
  readonly transactionType: InventoryTransactionType;
  readonly quantity: number;
  readonly quantityBefore: number;
  readonly quantityAfter: number;
  readonly reasonCode: string;
  readonly reasonDisplayName: string;
  readonly note: string | null;
  readonly performedByUserName: string;
  readonly createdAt: Date;
  readonly currentInventory: {
    readonly currentQuantity: number;
    readonly isLowStock: boolean;
    readonly isReorderRequired: boolean;
  };
}

/** 履歴レコード */
export interface TransactionHistoryItem {
  readonly id: number;
  readonly materialTypeId: number;
  readonly materialTypeName: string;
  readonly transactionType: InventoryTransactionType;
  readonly quantity: number;
  readonly quantityBefore: number;
  readonly quantityAfter: number;
  readonly reasonCode: string;
  readonly reasonDisplayName: string;
  readonly note: string | null;
  readonly performedByUserName: string;
  readonly createdAt: Date;
}

/** ページネーション情報 */
export interface PaginationInfo {
  readonly currentPage: number;
  readonly totalPages: number;
  readonly totalCount: number;
  readonly hasNextPage: boolean;
  readonly hasPreviousPage: boolean;
}

/** 履歴取得結果 */
export interface TransactionHistoryResult {
  readonly transactions: readonly TransactionHistoryItem[];
  readonly pagination: PaginationInfo;
}

/** 履歴取得クエリ */
export interface TransactionHistoryQuery {
  readonly materialTypeId?: number;
  readonly transactionType?: InventoryTransactionType;
  readonly startDate?: string;
  readonly endDate?: string;
  readonly page?: number;
  readonly limit?: number;
}

/** 在庫ステータス */
export const INVENTORY_STATUS = {
  LOW_STOCK: {
    key: 'lowStock',
    label: '在庫不足',
    color: 'error',
    badgeColor: THEME_COLORS.ERROR
  },
  REORDER_REQUIRED: {
    key: 'reorderRequired',
    label: '発注推奨',
    color: 'warning',
    badgeColor: THEME_COLORS.WARNING
  },
  NORMAL: {
    key: 'normal',
    label: '正常',
    color: 'success',
    badgeColor: THEME_COLORS.SUCCESS
  }
} as const;
